import { useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import adminService from '@wlb/shared/api/admin/adminService';
import type { AdminUser } from '@wlb/shared/api/admin/adminTypes';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { IconButton, IconLink } from '../components/ui/IconButton';
import { Pagination } from '../components/ui/Pagination';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { PageHeader } from '../components/layout/PageHeader';
import { useTriStateOptions, triStateValue } from '../features/users/triState';
import { formatDate } from '../utils/format';
import { resolvePictureUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

export function Users() {
  const { t } = useTranslation('admin');
  const location = useLocation();
  /**
   * Filters live in the URL, not in component state.
   *
   * That makes a filtered list linkable, survives a reload, and — with the
   * detail screen handing this query string back on its return link — lets an
   * operator open a member and come back to the page and search they were on
   * instead of a reset table.
   */
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const activeFilter = params.get('active') ?? '';
  const blockedFilter = params.get('blocked') ?? '';
  const verifiedFilter = params.get('verified') ?? '';
  const pageNumber = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const [pendingGuid, setPendingGuid] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search);
  const triStateOptions = useTriStateOptions();

  const setFilter = useCallback(
    (key: string, value: string) => {
      setParams(
        current => {
          const next = new URLSearchParams(current);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          // Narrowing the result set invalidates the page number: page 7 of the
          // old query is usually past the end of the new one.
          next.delete('page');
          return next;
        },
        // Typing in a search box must not push a history entry per keystroke.
        { replace: true },
      );
    },
    [setParams],
  );

  const goToPage = useCallback(
    (next: number) => {
      setParams(current => {
        const updated = new URLSearchParams(current);
        if (next <= 1) {
          updated.delete('page');
        } else {
          updated.set('page', String(next));
        }
        return updated;
      });
    },
    [setParams],
  );

  const load = useCallback(
    () =>
      adminService.getUsers({
        userName: debouncedSearch.trim(),
        isActive: triStateValue(activeFilter),
        isUserBlocked: triStateValue(blockedFilter),
        isEmailVerified: triStateValue(verifiedFilter),
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    [activeFilter, blockedFilter, debouncedSearch, pageNumber, verifiedFilter],
  );

  const requestKey = [
    debouncedSearch.trim(),
    activeFilter,
    blockedFilter,
    verifiedFilter,
    pageNumber,
  ].join('|');

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    true,
    'users',
  );

  const toggleActive = useCallback(
    async (user: AdminUser) => {
      setPendingGuid(user.userGuid);
      try {
        await adminService.setUserActiveStatus(user.userGuid, !user.isActive);
        ToastService.show({
          type: 'success',
          message: user.isActive
            ? t('users.toasts.deactivated')
            : t('users.toasts.activated'),
        });
        reload();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      } finally {
        setPendingGuid(null);
      }
    },
    [reload, t],
  );

  const columns = useMemo<readonly DataTableColumn<AdminUser>[]>(
    () => [
      {
        key: 'user',
        header: t('users.columns.user'),
        render: user => (
          <div className="ui-table__cell-with-avatar">
            <Avatar
              src={resolvePictureUrl(user.picture)}
              name={user.firstName || user.userName || t('common.none')}
            />
            <span>
              <span className="ui-table__primary">
                {user.firstName || user.userName || t('common.none')}
              </span>
              {user.userName ? (
                <span className="ui-table__secondary">{user.userName}</span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        header: t('users.columns.email'),
        render: user => (
          <span className="ui-table__truncate">{user.email || t('common.none')}</span>
        ),
      },
      {
        key: 'role',
        header: t('users.columns.role'),
        render: user => (user.role ? t(`roles.${user.role}`) : t('roles.unknown')),
      },
      {
        key: 'status',
        header: t('users.columns.status'),
        render: user =>
          user.isUserBlocked ? (
            <StatusPill label={t('status.blocked')} tone="danger" />
          ) : (
            <StatusPill
              label={user.isActive ? t('status.active') : t('status.inactive')}
              tone={user.isActive ? 'success' : 'neutral'}
            />
          ),
      },
      {
        key: 'verified',
        header: t('users.columns.verified'),
        render: user => (
          <StatusPill
            label={user.isEmailVerified ? t('status.verified') : t('status.unverified')}
            tone={user.isEmailVerified ? 'info' : 'warning'}
          />
        ),
      },
      {
        key: 'joined',
        header: t('users.columns.joined'),
        render: user => formatDate(user.joiningDate) || t('common.none'),
      },
      {
        key: 'lastActive',
        header: t('users.columns.lastActive'),
        render: user => formatDate(user.lastLoginOn) || t('common.none'),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: user => (
          <div className="ui-table__actions">
            {/* Each action carries its own hue so a row of them is scannable,
                and its own accessible name so the colour is never the only
                cue. The sub-modules are linked straight from the row: an
                operator chasing a report does not have to land on the profile
                first. */}
            <IconLink
              icon="view"
              tone="blue"
              to={`/users/${user.userGuid}`}
              state={{ listSearch: location.search }}
              label={t('users.actions.view')}
            />
            <IconLink
              icon="post"
              tone="teal"
              to={`/users/${user.userGuid}/posts`}
              state={{ listSearch: location.search }}
              label={t('users.actions.viewPosts')}
            />
            <IconLink
              icon="history"
              tone="indigo"
              to={`/users/${user.userGuid}/logins`}
              state={{ listSearch: location.search }}
              label={t('users.actions.viewLogins')}
            />
            <IconLink
              icon="power"
              tone="violet"
              to={`/users/${user.userGuid}/activation`}
              state={{ listSearch: location.search }}
              label={t('users.actions.viewActivation')}
            />
            <IconButton
              icon={user.isActive ? 'ban' : 'check'}
              tone={user.isActive ? 'rose' : 'emerald'}
              disabled={pendingGuid === user.userGuid}
              label={
                user.isActive
                  ? t('users.actions.deactivate')
                  : t('users.actions.activate')
              }
              onClick={() => toggleActive(user)}
            />
          </div>
        ),
      },
    ],
    [location.search, pendingGuid, t, toggleActive],
  );

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('users.searchPlaceholder')}
            value={search}
            onChange={event => setFilter('q', event.target.value)}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('users.filters.activeLabel')}
            options={triStateOptions}
            value={activeFilter}
            onChange={event => setFilter('active', event.target.value)}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('users.filters.blockedLabel')}
            options={triStateOptions}
            value={blockedFilter}
            onChange={event => setFilter('blocked', event.target.value)}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('users.filters.verifiedLabel')}
            options={triStateOptions}
            value={verifiedFilter}
            onChange={event => setFilter('verified', event.target.value)}
          />
        </div>
      </section>

      <DataTable
        caption={t('users.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={user => user.userGuid}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={reload}
        emptyTitle={t('users.emptyTitle')}
        emptyMessage={t('users.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage}
              hasNextPage={data.hasNextPage}
              pageSize={PAGE_SIZE}
              onChange={goToPage}
            />
          ) : null
        }
      />
    </div>
  );
}
